import {
  Camera, Eye, ImageIcon, MousePointerClick, QrCode, Send, Star,
} from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { registerPageLayout } from '../layout/registry.js'
import { LayoutEditControls, LayoutGrid, useLayoutState } from '../layout/LayoutGrid.js'
import { CommunicationDataProvider, useCommunication } from './data.js'
import { PERIODES } from './lib.js'
import {
  GoogleActionsWidget, GoogleReviewsWidget, GoogleVisibilityWidget,
  InstagramAudienceWidget, InstagramTopContentWidget,
  PublicationsUpcomingWidget,
} from './widgets.js'
import { AcquisitionHomeWidget } from './AcquisitionWidgets.js'
import { SITE_FAMILIES, MODULE_STATE_LABELS } from '../navigation/site-navigation.js'
import { ChevronRight } from 'lucide-react'
import { useMenuLien } from '../layout/menu-tuiles.js'

/**
 * /communication — LE COCKPIT (§4, §233).
 *
 * Page 100 % personnalisable via le Page Layout Engine existant : mêmes
 * gestes, même Save Bar, mêmes presets que le cockpit Soirée. AUCUN drawer
 * dans ce module (§1) — chaque « voir plus » est une vraie page.
 */

registerPageLayout({
  pageKey: 'communication-home',
  widgets: [
    { id: 'instagram-audience', Icon: Camera, label: 'Instagram — audience', description: 'Abonnés et évolution, relevés par Gestion', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: InstagramAudienceWidget },
    { id: 'google-visibility', Icon: Eye, label: 'Google — visibilité', description: 'Impressions Maps et Recherche de la fiche', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: GoogleVisibilityWidget },
    { id: 'google-actions', Icon: MousePointerClick, label: 'Google — actions clients', description: 'Itinéraires, appels, clics vers le site', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: GoogleActionsWidget },
    { id: 'instagram-top-content', Icon: ImageIcon, label: 'Contenus qui marchent', description: 'Posts et Reels classés par interactions réelles', minW: 3, minH: 3, defaultW: 6, defaultH: 4, Component: InstagramTopContentWidget },
    { id: 'google-reviews', Icon: Star, label: 'Avis Google', description: 'Note officielle et avis à traiter', minW: 3, minH: 3, defaultW: 6, defaultH: 4, Component: GoogleReviewsWidget },
    { id: 'publications-upcoming', Icon: Send, label: 'Publications', description: 'Programmées, en attente, brouillons', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: PublicationsUpcomingWidget },
    { id: 'acquisition-club', Icon: MousePointerClick, label: 'Acquisition Club', description: 'Clics des supports QR/NFC comptés par le tracker du club', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: AcquisitionHomeWidget },
  ],
  defaultLayout: [
    { id: 'instagram-audience', x: 0, y: 0, w: 4, h: 4 },
    { id: 'google-visibility', x: 4, y: 0, w: 4, h: 4 },
    { id: 'google-actions', x: 8, y: 0, w: 4, h: 4 },
    { id: 'instagram-top-content', x: 0, y: 4, w: 6, h: 4 },
    { id: 'google-reviews', x: 6, y: 4, w: 6, h: 4 },
    { id: 'publications-upcoming', x: 0, y: 8, w: 4, h: 4 },
    // §79 : synthèse légère DISPONIBLE, masquée par défaut.
    { id: 'acquisition-club', x: 0, y: 11, w: 4, h: 4, visibility: 'HIDDEN' },
  ],
})

/** Sélecteur de période — pilote TOUTES les tuiles à la fois (§92). */
function SelecteurPeriode() {
  const { periode, setPeriode } = useCommunication()
  return (
    <div className="lc-seg cm-periods" role="group" aria-label="Période">
      {PERIODES.map((p) => (
        <button key={p.value} type="button"
          className={`lc-seg__btn ${periode === p.value ? 'lc-seg__btn--on' : ''}`}
          aria-pressed={periode === p.value}
          onClick={() => setPeriode(p.value)}>
          {p.label}
        </button>
      ))}
    </div>
  )
}

function HomeContent() {
  const layout = useLayoutState('communication-home')
  const { data } = useCommunication()
  const menuLien = useMenuLien()
  return (
    <>
      {/* V2.1 §63 : le hub présente d'abord ses MODULES ; les widgets
          personnalisables en dessous restent des RÉSUMÉS de ces modules. */}
      <header className="cm-head">
        <div>
          <h1 className="lc-title">Communication</h1>
          <p className="lc-subtitle">
            Instagram et Google Business — audience, visibilité, avis et publications.
            {data?.safeMode ? ' Mode sûr actif : les écritures externes sont retenues.' : ''}
          </p>
        </div>
        <div className="cm-head__actions">
          <SelecteurPeriode />
          <Link href="/communication/liens" className="lc-btn">
            <QrCode size={14} aria-hidden="true" /> Liens &amp; QR/NFC
          </Link>
          <Link href="/communication/publications/nouvelle" className="lc-btn lc-btn--primary">
            <Send size={14} aria-hidden="true" /> Nouvelle publication
          </Link>
          <LayoutEditControls state={layout} />
        </div>
      </header>
      <div className="fh-grid cm-modules">
        {SITE_FAMILIES.find((f) => f.id === 'communication')!.children.map((m) => {
          const corps = (
            <>
              <span className="lc-card__edge" aria-hidden="true" />
              <span className="lc-card__icon" aria-hidden="true"><m.icon /></span>
              <div className="fh-tile__texte">
                <h2>{m.label}</h2>
                <p className="lc-hint">{m.description}</p>
              </div>
              <div className="fh-tile__pied">
                {m.state === 'PLANNED' ? <span className="lc-pill lc-pill--idle fh-tile__avenir">{MODULE_STATE_LABELS.PLANNED}</span> : null}
                {m.route ? <span className="fh-tile__cta"><ChevronRight size={16} aria-hidden="true" /></span> : null}
              </div>
            </>
          )
          if (!m.route) return <div key={m.id} className="lc-card fh-tile fh-tile--muette lc-in">{corps}</div>
          return <Link key={m.id} href={m.route} className="lc-card fh-tile lc-in" {...menuLien(m.route, m.label)}>{corps}</Link>
        })}
      </div>
      {layout.loaded ? <LayoutGrid pageKey="communication-home" state={layout} /> : null}
    </>
  )
}

export function CommunicationHomePage() {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <CommunicationDataProvider>
          <HomeContent />
        </CommunicationDataProvider>
      </main>
    </Shell>
  )
}
